import { z } from 'zod';
/**
 * Phase 5b — fine-grained permission matrix.
 *
 * The matrix maps (role, section, action) → allowed. Stored per-customer
 * so each tenant can tweak their own; defaults are seeded on first read.
 *
 * `admin` always has every permission (enforced server-side; the matrix is
 * advisory for admins and authoritative for member/viewer).
 */
export const PermissionRoleSchema = z.enum(['admin', 'member', 'viewer']);
export const PermissionSectionSchema = z.enum([
    'events',
    'campaigns',
    'contacts',
    'templates',
    'webhooks',
    'email_accounts',
    'sms_providers',
    'service_profiles',
    'customer_config',
    'users',
    'permissions',
    'training',
]);
export const PermissionActionSchema = z.enum(['view', 'add', 'edit', 'delete']);
export const PermissionEntrySchema = z.object({
    role: PermissionRoleSchema,
    section: PermissionSectionSchema,
    action: PermissionActionSchema,
    allowed: z.boolean(),
});
export const PermissionMatrixSchema = z.object({
    customer_id: z.string(),
    entries: z.array(PermissionEntrySchema),
});
export const PermissionMatrixUpdateSchema = z.object({
    entries: z.array(PermissionEntrySchema).max(500),
});
export const ALL_SECTIONS = PermissionSectionSchema.options;
export const ALL_ACTIONS = PermissionActionSchema.options;
export const ALL_ROLES = PermissionRoleSchema.options;
/**
 * Default permissions seeded on first read for a customer. Admin gets
 * everything; member gets full access to operational sections (no delete on
 * sensitive ones); viewer gets read-only.
 */
export function defaultPermissionMatrix() {
    const out = [];
    for (const section of ALL_SECTIONS) {
        for (const action of ALL_ACTIONS) {
            // Admin: all allowed
            out.push({ role: 'admin', section, action, allowed: true });
            // Viewer: view only
            out.push({ role: 'viewer', section, action, allowed: action === 'view' });
            // Member: all CRUD on operational sections; view-only on admin-y ones
            const memberAllowed = action === 'view'
                ? true
                : ['users', 'permissions', 'customer_config', 'training'].includes(section)
                    ? false
                    : true;
            out.push({ role: 'member', section, action, allowed: memberAllowed });
        }
    }
    return out;
}
export function permissionKey(section, action) {
    return `${section}:${action}`;
}
//# sourceMappingURL=permissions.js.map